import React, { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Switch,
  Tag,
  Button,
  Space,
  message,
  Typography,
  Divider,
} from 'antd';
import { PromptTemplate } from '../../types';
import { promptTemplatesApi, UpdatePromptTemplateRequest } from '../../services/api/promptTemplates';

const { TextArea } = Input;
const { Text } = Typography;

interface EditPromptTemplateModalProps {
  visible: boolean;
  template: PromptTemplate | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditPromptTemplateModal: React.FC<EditPromptTemplateModalProps> = ({
  visible,
  template,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState<string>('');
  const [previewError, setPreviewError] = useState<string>('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (visible && template) {
      form.setFieldsValue({
        name: template.name,
        description: template.description,
        template: template.template,
        variables: JSON.stringify(template.variables || []),
        expectedOutput: template.expectedOutput,
        isActive: template.isActive,
      });
      setPreviewResult('');
      setPreviewError('');
      setShowPreview(false);
    } else {
      form.resetFields();
      setPreviewResult('');
      setPreviewError('');
      setShowPreview(false);
    }
  }, [visible, template, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      let variables: string[] = [];
      try {
        variables = JSON.parse(values.variables || '[]');
        if (!Array.isArray(variables)) {
          throw new Error('Variables must be an array');
        }
      } catch (e) {
        message.error('Variables must be a valid JSON array');
        setLoading(false);
        return;
      }

      const updateData: UpdatePromptTemplateRequest = {
        name: values.name?.trim(),
        description: values.description?.trim(),
        template: values.template,
        variables,
        expectedOutput: values.expectedOutput?.trim(),
        isActive: values.isActive,
      };

      await promptTemplatesApi.updatePromptTemplate(template!.id, updateData);

      message.success('Prompt template updated successfully');
      onSuccess();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.message || 'Failed to update prompt template');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      setPreviewError('');
      setPreviewResult('');

      // Get sample data from form or use default
      const sampleDataStr = prompt(
        'Enter sample data (JSON format):',
        JSON.stringify({
          storyName: 'Example Story',
          author: 'Example Author',
          chapters: [
            { name: 'Chapter 1', content: 'Sample content...' },
          ],
        }, null, 2)
      );

      if (!sampleDataStr) {
        setPreviewLoading(false);
        return;
      }

      let sampleData: Record<string, any> = {};
      try {
        sampleData = JSON.parse(sampleDataStr);
      } catch (e) {
        message.error('Invalid JSON format');
        setPreviewLoading(false);
        return;
      }

      const response = await promptTemplatesApi.previewTemplate(template!.id, sampleData);

      if (response.data.valid) {
        setPreviewResult(response.data.rendered || '');
        setShowPreview(true);
      } else {
        setPreviewError(response.data.error || 'Unknown error');
        setShowPreview(true);
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to preview template');
      setPreviewError(error.message || 'Failed to preview');
      setShowPreview(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Modal
      title="Edit Prompt Template"
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="preview" onClick={handlePreview} loading={previewLoading}>
          Preview with Sample Data
        </Button>,
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Save
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Code">
          <Tag color="blue">{template?.code}</Tag>
        </Form.Item>

        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please enter template name' }]}
        >
          <Input placeholder="Enter template name" />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <TextArea
            rows={3}
            placeholder="Describe the purpose of this template"
          />
        </Form.Item>

        <Form.Item
          label="Template (Mustache Syntax)"
          name="template"
          rules={[{ required: true, message: 'Please enter template content' }]}
          extra="Use {{variableName}} for variables, {{#array}}...{{/array}} for loops"
        >
          <TextArea
            rows={15}
            placeholder="Enter template using Mustache syntax..."
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>

        <Form.Item
          label="Variables (JSON Array)"
          name="variables"
          rules={[{ required: true, message: 'Please enter variables array' }]}
          extra='Example: ["storyName", "author", "chapters"]'
        >
          <Input placeholder='["variable1", "variable2"]' />
        </Form.Item>

        <Form.Item label="Expected Output" name="expectedOutput">
          <TextArea
            rows={3}
            placeholder="Describe expected JSON structure..."
            style={{ fontFamily: 'monospace' }}
          />
        </Form.Item>

        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>

        {showPreview && (
          <>
            <Divider />
            <div>
              <Text strong>Preview Result:</Text>
              {previewError ? (
                <div style={{ 
                  marginTop: 8, 
                  padding: 12, 
                  background: '#fff2f0', 
                  border: '1px solid #ffccc7',
                  borderRadius: 4,
                  color: '#cf1322'
                }}>
                  Error: {previewError}
                </div>
              ) : (
                <TextArea
                  value={previewResult}
                  rows={10}
                  readOnly
                  style={{ 
                    marginTop: 8, 
                    fontFamily: 'monospace',
                    background: '#f5f5f5'
                  }}
                />
              )}
            </div>
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EditPromptTemplateModal;
